import {
  InstantPayTutorialStep,
  InstantPayTutorialStepEnum,
} from "components/Tutorials/InstantPayTutorial.tsx/InstantPayTutorial";
import { RiVerifiedBadgeFill } from "react-icons/ri";
import { BiQrScan } from "react-icons/bi";
import { FaRegCreditCard } from "react-icons/fa";
import { PiQuestion } from "react-icons/pi";
import { mutedText } from "styles";
import useIsMobile from "hooks/useIsMobile";
import Render from "components/Common/Render";

interface IStepperProps {
  steps: Array<InstantPayTutorialStep>;
  selectedStep: InstantPayTutorialStep;
  setSelectedStep: (value: InstantPayTutorialStep) => void;
  lastCompletedStep: number;
}

const Stepper: React.FC<IStepperProps> = ({
  steps,
  selectedStep,
  lastCompletedStep,
}) => {
  const isMobile = useIsMobile();

  const isStepCompleted = (stepId: number) => lastCompletedStep >= stepId;
  const isSelectedStep = (stepId: number) => stepId == selectedStep.id;

  const getIcon = (step: InstantPayTutorialStep) => {
    const iconClass = isStepCompleted(step.id) ? "text-green text-2xl" : "";
    const iconSize = 20;
    switch (step.step) {
      case InstantPayTutorialStepEnum.QRC0DE:
        return <PiQuestion className={iconClass} size={iconSize} />;
      case InstantPayTutorialStepEnum.SETUP:
        return <FaRegCreditCard className={iconClass} size={iconSize} />;
      case InstantPayTutorialStepEnum.ACCESS:
        return <BiQrScan className={iconClass} size={iconSize} />;
      default:
        return null;
    }
  };

  const getColor = (stepId: number) => {
    if (isStepCompleted(stepId)) {
      return "green";
    } else {
      return "black";
    }
  };

  return (
    <>
      {/* Mobile View */}
      <Render when={isMobile}>
        <div className="w-full flex flex-col">
          {/* Step Title */}
          <div className="flex gap-3 items-center">
            <div className={` self-center text-${getColor(selectedStep.id)}`}>
              {getIcon(selectedStep)}
            </div>
            <div
              className={`font-semibold text-sm  bg-opacity-20  w-full ${
                isStepCompleted(selectedStep.id) ? "text-green" : "text-black"
              }`}
            >
              {selectedStep.label}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center justify-between w-full py-3">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center ${
                  index < steps.length - 1 ? "w-full" : ""
                }`}
              >
                {isStepCompleted(step?.id) ? (
                  <RiVerifiedBadgeFill className="text-xl text-green justify-end align-self-end flex" />
                ) : (
                  <div
                    className={`rounded-full w-3 h-3 flex items-center justify-center ${
                      isSelectedStep(step.id) ? "border-skyBlue" : "bg-gray-300"
                    } border-2 ${
                      isStepCompleted(step.id)
                        ? "border-green text-white"
                        : "border-skyblue"
                    }`}
                  ></div>
                )}

                {/* Horizontal Line (except last step) */}
                {index < steps.length - 1 && (
                  <div
                    className={` w-full mx-2  h-[2px] ${
                      isStepCompleted(step.id)
                        ? "bg-green"
                        : isSelectedStep(step.id)
                        ? "bg-skyBlue"
                        : "bg-gray-300"
                    } `}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </Render>

      {/* DESKTOP VIEW (Vertical Stepper) */}
      <Render when={!isMobile}>
        <div className="w-fit flex flex-col md:min-w-[340px]">
          <p className="text-black font-semibold text-xs bg-skyBlue bg-opacity-10 p-2">
            Complete the following steps
          </p>
          {steps?.map((step: InstantPayTutorialStep) => (
            <div
              className={`${mutedText} p-2 border border-color-skyBlue items-center flex gap-4 ${
                selectedStep?.id === step?.id
                  ? "bg-skyBlue bg-opacity-30 font-semibold"
                  : ""
              }`}
            >
              {getIcon(step)}
              <div className={`flex-1 text-${getColor(step?.id)}`}>
                {step.label}
              </div>
              {isStepCompleted(step?.id) ? (
                <RiVerifiedBadgeFill className="text-xl text-green justify-end align-self-end flex" />
              ) : null}
            </div>
          ))}
        </div>
      </Render>
    </>
  );
};

export default Stepper;
